import {
  type GameUiLook,
  UI_LIMITS,
  type UiKey,
  type UiNumberKey,
  type UiScreen,
  type UiSize,
  type UiTarget,
  type UiValues,
  type UiView,
  type UiViews,
} from '@mogzo/shared';

/** The Game UI editor's names and settings (the approved page-builder mockup, M19). */

export const TARGET_NAMES: Record<UiTarget, string> = {
  theme: 'Theme',
  popup: 'Achievement pop-up',
  leaderboard: 'Leaderboard',
  achievements: 'Achievements',
  pause: 'Pause menu',
  signin: 'Sign-in prompt',
  match: 'Multiplayer',
};
export const SIZE_NAMES: Record<UiSize, string> = {
  computer: 'Computer',
  tablet: 'Tablet',
  phone: 'Phone',
};
export const SCREEN_NAMES: Record<UiScreen, string> = { splash: 'Splash', game: 'Game' };

export function viewName(view: UiView): string {
  const [size, screen] = view.split('.') as [UiSize, UiScreen];
  return `${SIZE_NAMES[size]} · ${SCREEN_NAMES[screen]}`;
}

interface NumberSpec {
  label: string;
  help?: string;
  /** Shown when a typed value is under the limit. */
  minMsg?: string;
}
export const NUMBER_SPECS: Record<UiNumberKey, NumberSpec> = {
  dim: { label: 'Dim the game behind panels' },
  textSize: {
    label: 'Text size',
    minMsg: 'Text never goes below 13 px, so it stays readable.',
  },
  btnHeight: {
    label: 'Button height',
    minMsg: 'Buttons stay at least 44 px tall, so thumbs can hit them.',
  },
  btnRadius: {
    label: 'Button corners',
    help: 'Half the button height or more makes them fully round.',
  },
  pad: { label: 'Space inside' },
  gap: { label: 'Space between items' },
  progress: { label: 'Progress bar thickness' },
  offset: { label: 'Distance from the edge', help: 'To keep your game’s own score in view.' },
  width: { label: 'Width', help: 'Never wider than the screen; it shrinks to fit a small frame.' },
  height: { label: 'Height', help: 'Of the screen. On a phone it slides up from the bottom.' },
  seconds: { label: 'Shows for' },
  rowHeight: { label: 'Row height' },
  iconSize: { label: 'Icon size' },
};

type ChoiceKey = 'edge' | 'scrollbar' | 'switches' | 'badges';
export const CHOICE_SPECS: {
  [K in ChoiceKey]: { label: string; options: [UiValues[K], string][] };
} = {
  edge: {
    label: 'Position',
    options: [
      ['top', 'Top'],
      ['bottom', 'Bottom'],
    ],
  },
  scrollbar: {
    label: 'Scroll bars',
    options: [
      ['thin', 'Thin'],
      ['hidden', 'Hidden'],
      ['standard', 'Standard'],
    ],
  },
  switches: {
    label: 'Switch shape',
    options: [
      ['round', 'Round'],
      ['square', 'Square'],
    ],
  },
  badges: {
    label: 'Top three',
    options: [
      ['accent', 'Highlighted'],
      ['plain', 'Plain'],
    ],
  },
};

export function isChoice(key: UiKey): key is ChoiceKey {
  return key in CHOICE_SPECS;
}
export function limits(key: UiNumberKey) {
  return UI_LIMITS[key];
}

export interface Section {
  id: string;
  title: string;
  keys: UiKey[];
  /** Theme's colours and presets, or its font: the same in every view. */
  colours?: boolean;
  font?: boolean;
}

/** The settings panel's sections for what's being styled; values a phone has no use for are left out. */
export function sectionsFor(target: UiTarget, phone: boolean): Section[] {
  const size: UiKey[] = phone ? ['height'] : ['width'];
  const sized = (sections: Section[]) =>
    phone ? sections : [{ id: 'size', title: 'Size', keys: ['width'] } as Section, ...sections];
  switch (target) {
    case 'theme':
      return [
        { id: 'colours', title: 'Colours', keys: [], colours: true },
        { id: 'type', title: 'Typography', keys: ['textSize'], font: true },
        { id: 'shape', title: 'Shape', keys: ['corners', 'btnRadius'] },
        { id: 'space', title: 'Size and spacing', keys: ['btnHeight', 'pad', 'gap'] },
        { id: 'details', title: 'Details', keys: ['switches', 'scrollbar', 'progress', 'dim'] },
      ];
    case 'popup':
      return [
        {
          id: 'place',
          title: 'Position',
          keys: phone ? ['edge', 'offset'] : ['edge', 'offset', 'width'],
        },
        { id: 'shape', title: 'Shape', keys: ['corners'] },
        { id: 'space', title: 'Spacing and text', keys: ['pad', 'textSize'] },
        { id: 'timing', title: 'Timing', keys: ['seconds'] },
      ];
    case 'leaderboard':
      return [
        { id: 'size', title: 'Size', keys: size },
        { id: 'shape', title: 'Shape', keys: ['corners'] },
        { id: 'space', title: 'Spacing and text', keys: ['pad', 'textSize'] },
        { id: 'rows', title: 'Rows', keys: ['rowHeight', 'badges', 'scrollbar'] },
      ];
    case 'achievements':
      return [
        { id: 'size', title: 'Size', keys: size },
        { id: 'shape', title: 'Shape', keys: ['corners'] },
        { id: 'space', title: 'Spacing and text', keys: ['pad', 'textSize'] },
        { id: 'list', title: 'List', keys: ['iconSize', 'progress', 'scrollbar'] },
      ];
    case 'pause':
      return sized([
        { id: 'shape', title: 'Shape', keys: ['corners', 'btnRadius'] },
        { id: 'space', title: 'Buttons and spacing', keys: ['btnHeight', 'pad', 'gap'] },
        { id: 'text', title: 'Text and switches', keys: ['textSize', 'switches'] },
      ]);
    case 'match':
      return sized([
        { id: 'shape', title: 'Shape', keys: ['corners', 'btnRadius'] },
        { id: 'space', title: 'Buttons and spacing', keys: ['btnHeight', 'pad', 'gap'] },
        { id: 'text', title: 'Text', keys: ['textSize'] },
      ]);
    case 'signin':
      return sized([
        { id: 'shape', title: 'Shape', keys: ['corners', 'btnRadius'] },
        { id: 'space', title: 'Buttons and spacing', keys: ['btnHeight', 'pad'] },
        { id: 'text', title: 'Text', keys: ['textSize'] },
      ]);
  }
}

/** A look with one value set in a view (or, with `undefined`, taken from the views above again). */
export function withValue<K extends UiKey>(
  look: GameUiLook,
  view: UiView,
  target: UiTarget,
  key: K,
  value: UiValues[K] | undefined,
): GameUiLook {
  const views: UiViews = { ...look.views };
  const own = { ...views[view] };
  const values: Partial<UiValues> = { ...own[target] };
  if (value === undefined) delete values[key];
  else values[key] = value;
  if (Object.keys(values).length) own[target] = values;
  else delete own[target];
  if (Object.keys(own).length) views[view] = own;
  else delete views[view];
  return { ...look, views };
}
